import { z } from 'zod';

// ============================================================================
// UNIFIED ERROR RESPONSE SCHEMAS
// ============================================================================

// Validation error detail for field-specific errors
export const ValidationErrorDetailSchema = z.object({
  field: z.string(),
  message: z.string(),
  value: z.unknown().optional(),
});

// Standard error response schema
export const ErrorResponseSchema = z.object({
  type: z.literal('error'),
  error: z.string(),
  code: z.string(),
  message: z.string(),
  details: z.array(ValidationErrorDetailSchema).optional(),
  timestamp: z.string().optional(),
});

// Success response wrapper schema
export const SuccessResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    type: z.literal('success'),
    data: dataSchema,
  });

// Generic API response schema
export const ApiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.discriminatedUnion('type', [
    SuccessResponseSchema(dataSchema),
    ErrorResponseSchema,
  ]);

// ============================================================================
// COMMON ERROR CODES
// ============================================================================

export const ErrorCodes = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  BAD_REQUEST: 'BAD_REQUEST',
  CONFLICT: 'CONFLICT',
} as const;

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type ValidationErrorDetail = z.infer<typeof ValidationErrorDetailSchema>;
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
export type SuccessResponse<T> = {
  type: 'success';
  data: T;
};
export type ApiResponse<T> = SuccessResponse<T> | ErrorResponse;
